import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useLocaleFormat } from '@/i18n/useLocaleFormat'
import { Button, Card, CardContent, SelectableCard } from '@/components/ui'
import { useArenaAgents, useArenaRuns, useArenaStyles, useCreateArenaRun } from '@/api/arenaHooks'
import type { ArenaAgent, JsonValue } from '@/api/arena'

function statusPillClass(status: string): string {
  switch (status) {
    case 'done':
    case 'success':
      return 'bg-emerald-500/15 text-emerald-400'
    case 'partial':
      return 'bg-amber-500/15 text-amber-400'
    case 'failed':
      return 'bg-red-500/15 text-red-400'
    default:
      return 'bg-foreground/10 text-foreground/70'
  }
}

export default function ArenaPage() {
  const { t } = useTranslation('pages')
  const { formatDateTime } = useLocaleFormat()
  const navigate = useNavigate()
  const agentsQuery = useArenaAgents()
  const runsQuery = useArenaRuns()
  const createRun = useCreateArenaRun()
  // Only the styles the arena can actually resolve (fixtures locally, live
  // public styles in prod) — so the dropdown never offers unsupported ones.
  const stylesQuery = useArenaStyles()
  const styleOptions = useMemo(() => stylesQuery.data?.styles ?? [], [stylesQuery.data])

  const agents = useMemo(() => agentsQuery.data?.agents ?? [], [agentsQuery.data])
  const [agentType, setAgentType] = useState<string | null>(null)
  const [models, setModels] = useState<string[]>([])
  const [inputs, setInputs] = useState<Record<string, string>>({})
  // Reference images live outside `inputs` (it's a string map) — in_images is a
  // list: 1 ref for reference modes, up to 2 (first + last frame) for FIRST_LAST.
  const [refImages, setRefImages] = useState<string[]>([])
  const [blind, setBlind] = useState(false)

  const agent: ArenaAgent | null = useMemo(
    () => agents.find((a) => a.agent_type === agentType) ?? null,
    [agents, agentType],
  )

  // Model picker: render the full choice list but disable models not supported in
  // the current mode (mirrors the backend's runnable set). model_support is null
  // for non-video agents → every choice stays selectable.
  const currentMode = inputs['in_mode'] || agent?.default_mode || ''
  const modelChoices = useMemo(() => agent?.model_choices ?? agent?.models ?? [], [agent])
  const supportedModels = useMemo<string[] | null>(() => {
    if (!agent?.model_support) return null
    return agent.model_support[currentMode] ?? []
  }, [agent, currentMode])
  const isModelDisabled = (m: string) => !!supportedModels && !supportedModels.includes(m)

  // Drop any now-unsupported model from the selection when the mode changes.
  useEffect(() => {
    if (!supportedModels) return
    setModels((prev) => prev.filter((m) => supportedModels.includes(m)))
  }, [supportedModels])

  // Keep in_style on a style the arena can resolve (the catalog default may not
  // be available locally). Prefer the field default if supported, else the first.
  useEffect(() => {
    if (!agent || styleOptions.length === 0) return
    const styleField = agent.input_fields.find((f) => f.id === 'in_style')
    if (!styleField) return
    setInputs((prev) => {
      if (prev.in_style && styleOptions.some((o) => o.value === prev.in_style)) return prev
      const def = typeof styleField.default === 'string' ? styleField.default : ''
      const pick = styleOptions.some((o) => o.value === def) ? def : styleOptions[0].value
      return { ...prev, in_style: pick }
    })
  }, [agent, styleOptions])

  function selectAgent(a: ArenaAgent) {
    setAgentType(a.agent_type)
    setModels([])
    setRefImages([])
    // Seed inputs with each field's default so defaults (e.g. style) are shown AND submitted.
    const init: Record<string, string> = {}
    for (const f of a.input_fields) {
      if (typeof f.default === 'string' && f.default) init[f.id] = f.default
    }
    setInputs(init)
  }

  function toggleModel(m: string) {
    if (isModelDisabled(m)) return
    setModels((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]))
  }

  const missingRequired = useMemo(() => {
    if (!agent) return true
    return agent.input_fields.some((f) => f.required && !inputs[f.id]?.trim())
  }, [agent, inputs])

  const canRun = !!agent && models.length >= 1 && !missingRequired && !createRun.isPending

  async function run() {
    if (!agent) return
    const input: Record<string, JsonValue> = {}
    for (const f of agent.input_fields) {
      if (f.type === 'image_upload') continue // handled via refImages below
      const raw = inputs[f.id]
      if (raw === undefined || raw === '') continue
      input[f.id] = f.type === 'number' ? Number(raw) : raw
    }
    const imgField = agent.input_fields.find((f) => f.type === 'image_upload')
    if (imgField && refImages.length) input[imgField.id] = refImages
    const res = await createRun.mutateAsync({ agent_type: agent.agent_type, models, input, blind })
    navigate(`/arena/${res.run_id}`)
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-foreground">{t('arena.title')}</h1>
      </div>

      {/* New comparison */}
      <Card className="mb-8">
        <CardContent className="space-y-6 p-6">
          <div>
            <div className="mb-2 text-sm font-medium text-foreground/80">{t('arena.pickAgent')}</div>
            {agentsQuery.isLoading ? (
              <div className="text-sm text-foreground/60">{t('arena.loadingAgents')}</div>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {agents.map((a) => (
                  <SelectableCard
                    key={a.agent_type}
                    title={a.label}
                    description={a.model_dimension === 'image' ? t('arena.imageModel') : t('arena.llmFamily')}
                    selected={a.agent_type === agentType}
                    onClick={() => selectAgent(a)}
                  >
                    <span className="sr-only">{a.label}</span>
                  </SelectableCard>
                ))}
              </div>
            )}
          </div>

          {agent && (
            <>
              <div>
                <div className="mb-2 text-sm font-medium text-foreground/80">
                  {t('arena.pickModels')}
                </div>
                <div className="flex flex-wrap gap-2">
                  {modelChoices.map((m) => {
                    const on = models.includes(m)
                    const disabled = isModelDisabled(m)
                    return (
                      <button
                        key={m}
                        type="button"
                        disabled={disabled}
                        title={disabled ? t('arena.notSupportedInMode', { mode: currentMode }) : undefined}
                        onClick={() => toggleModel(m)}
                        className={
                          'rounded-full border px-3 py-1 text-sm transition-colors ' +
                          (disabled
                            ? 'cursor-not-allowed border-border-subtle text-foreground/25 line-through'
                            : on
                              ? 'border-primary/40 bg-primary/10 text-primary'
                              : 'border-border-subtle text-foreground/70 hover:border-border')
                        }
                      >
                        {m}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div>
                <div className="mb-2 text-sm font-medium text-foreground/80">{t('arena.inputStep')}</div>
                <p className="mb-3 text-xs font-medium text-foreground/50">
                  {t('arena.identicalInput')}
                </p>
                <div className="space-y-3">
                  {agent.input_fields.map((f) => (
                    <label key={f.id} className="block">
                      <span className="mb-1 block text-xs font-medium text-foreground/60">
                        {f.label}
                        {f.required ? ' *' : ''}
                      </span>
                      {f.id === 'in_style' ? (
                        <select
                          value={inputs[f.id] ?? (typeof f.default === 'string' ? f.default : '')}
                          onChange={(e) => setInputs((p) => ({ ...p, [f.id]: e.target.value }))}
                          className="w-full rounded-md border border-border-subtle bg-transparent p-2 text-sm text-foreground"
                        >
                          {styleOptions.map((s) => (
                            <option key={s.value} value={s.value} className="bg-surface text-foreground">
                              {s.label}
                            </option>
                          ))}
                        </select>
                      ) : f.type === 'select' ? (
                        <select
                          value={inputs[f.id] ?? (typeof f.default === 'string' ? f.default : '')}
                          onChange={(e) => setInputs((p) => ({ ...p, [f.id]: e.target.value }))}
                          className="w-full rounded-md border border-border-subtle bg-transparent p-2 text-sm text-foreground"
                        >
                          {(f.options ?? []).map((opt) => (
                            <option key={opt} value={opt} className="bg-surface text-foreground">
                              {opt}
                            </option>
                          ))}
                        </select>
                      ) : f.type === 'image_upload' ? (
                        <div className="space-y-2">
                          <input
                            type="file"
                            accept="image/*"
                            multiple={currentMode === 'FIRST_LAST'}
                            disabled={refImages.length >= 2}
                            onChange={(e) => {
                              const files = Array.from(e.target.files ?? [])
                              if (!files.length) return
                              Promise.all(
                                files.map(
                                  (file) =>
                                    new Promise<string>((resolve) => {
                                      const reader = new FileReader()
                                      reader.onload = () => resolve(String(reader.result))
                                      reader.readAsDataURL(file)
                                    }),
                                ),
                              ).then((urls) => setRefImages((prev) => [...prev, ...urls].slice(0, 2)))
                              e.target.value = ''
                            }}
                            className="block w-full text-sm text-foreground/70 file:mr-3 file:rounded-md file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-primary disabled:opacity-40"
                          />
                          <p className="text-xs font-medium text-foreground/50">
                            {currentMode === 'FIRST_LAST'
                              ? t('arena.uploadTwoFrames')
                              : t('arena.optionalUpload')}
                          </p>
                          {refImages.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {refImages.map((src, i) => (
                                <div key={i} className="relative">
                                  <img
                                    src={src}
                                    alt={t('arena.referenceAlt', { index: i + 1 })}
                                    className="max-h-24 rounded-md border border-border-subtle"
                                  />
                                  {currentMode === 'FIRST_LAST' && (
                                    <span className="absolute left-1 top-1 rounded bg-black/60 px-1 text-xxs text-white">
                                      {i === 0 ? t('arena.firstFrame') : t('arena.lastFrame')}
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    aria-label={t('arena.removeReference')}
                                    onClick={() =>
                                      setRefImages((prev) => prev.filter((_, j) => j !== i))
                                    }
                                    className="absolute right-1 top-1 rounded bg-black/60 px-1 text-xxs text-white"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : f.type === 'textarea' ? (
                        <textarea
                          value={inputs[f.id] ?? ''}
                          onChange={(e) => setInputs((p) => ({ ...p, [f.id]: e.target.value }))}
                          rows={3}
                          className="w-full rounded-md border border-border-subtle bg-transparent p-2 text-sm text-foreground"
                        />
                      ) : (
                        <input
                          type={f.type === 'number' ? 'number' : 'text'}
                          value={inputs[f.id] ?? ''}
                          onChange={(e) => setInputs((p) => ({ ...p, [f.id]: e.target.value }))}
                          className="w-full rounded-md border border-border-subtle bg-transparent p-2 text-sm text-foreground"
                        />
                      )}
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-foreground/70">
                  <input type="checkbox" checked={blind} onChange={(e) => setBlind(e.target.checked)} />
                  {t('arena.blindComparison')}
                </label>
                <Button onClick={run} disabled={!canRun}>
                  {createRun.isPending
                    ? t('arena.starting')
                    : models.length >= 2
                      ? t('arena.runComparison', { count: models.length })
                      : t('arena.run')}
                </Button>
              </div>
              {createRun.isError && (
                <div className="text-sm text-red-400">{String(createRun.error)}</div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Recent runs */}
      <div className="mb-3 text-sm font-medium text-foreground/80">{t('arena.recentComparisons')}</div>
      <div className="space-y-2">
        {runsQuery.data?.runs?.length ? (
          runsQuery.data.runs.map((r) => (
            <Link
              key={r.id}
              to={`/arena/${r.id}`}
              className="flex items-center justify-between rounded-md border border-border-subtle px-4 py-3 text-sm hover:border-border"
            >
              <span className="text-foreground">{r.agent_type}</span>
              <span className="text-foreground/50">{t('arena.nModels', { count: r.models.length })}</span>
              <span className={'rounded-full px-2 py-0.5 text-xs font-medium ' + statusPillClass(r.status)}>
                {r.status}
              </span>
              <span className="text-foreground/40">{formatDateTime(r.created_at)}</span>
            </Link>
          ))
        ) : (
          <div className="text-sm text-foreground/50">{t('arena.noComparisons')}</div>
        )}
      </div>
    </div>
  )
}
